import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Search,
  Plus,
  ExternalLink,
  Trash2,
  Copy,
  Check,
  Eye,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Globe,
  Inbox,
  CheckCircle2,
  CircleDashed,
  MoreHorizontal,
  Play,
  Pause,
  FileText,
  BarChart3,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { useFeatures } from '@/client/hooks/use-features';
import { formatNumber, copyToClipboard } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { OnboardingEmptyState, SearchEmptyState } from '@/client/components/ui/empty-state';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Input } from '@/client/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/client/components/ui/dropdown-menu';
import type { FormResponse } from '@/shared/schemas/forms';

// =============================================================================
// Form Card Component
// =============================================================================

function FormCard({
  form,
  onEdit,
  onDelete,
  onTogglePublish,
  onToggleResponses,
}: {
  form: FormResponse;
  onEdit: (form: FormResponse) => void;
  onDelete: (form: FormResponse) => void;
  onTogglePublish: (form: FormResponse) => void;
  onToggleResponses: (form: FormResponse) => void;
}) {
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}/f/${form.slug}`;

  const handleCopy = async () => {
    if (!form.isPublished) {
      toast.error('Form must be published to copy URL');
      return;
    }
    await copyToClipboard(publicUrl);
    setCopied(true);
    toast.success('URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const responseRate =
    form.totalViews > 0 ? Math.round((form.totalSubmissions / form.totalViews) * 100) : 0;

  return (
    <Card className="group relative rounded-2xl overflow-visible border bg-card hover:shadow-lg transition-all duration-300">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg flex-shrink-0">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-base line-clamp-1">{form.name}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                {form.isPublished ? (
                  <Badge className="text-[10px] px-1.5 py-0 bg-emerald-500 hover:bg-emerald-600 text-white border-0">
                    <Globe className="h-2.5 w-2.5 mr-0.5" />
                    Live
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    Draft
                  </Badge>
                )}
                {form.project && (
                  <span className="text-xs text-muted-foreground truncate">
                    {form.project.name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Accepting responses indicator */}
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {form.isAcceptingResponses ? (
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="text-[10px] font-medium">Open</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-zinc-400">
                <CircleDashed className="h-3.5 w-3.5" />
                <span className="text-[10px] font-medium">Closed</span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {form.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{form.description}</p>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
              <Eye className="h-3 w-3" />
            </div>
            <p className="text-sm font-bold tabular-nums">{formatNumber(form.totalViews)}</p>
            <p className="text-[10px] text-muted-foreground">Views</p>
          </div>
          <Link
            to={`/forms/${form.id}/submissions`}
            className="text-center p-2 bg-muted/50 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors cursor-pointer group/stat"
          >
            <div className="flex items-center justify-center gap-1 text-muted-foreground group-hover/stat:text-emerald-600 mb-0.5">
              <Inbox className="h-3 w-3" />
            </div>
            <p className="text-sm font-bold tabular-nums group-hover/stat:text-emerald-600">
              {formatNumber(form.totalSubmissions)}
            </p>
            <p className="text-[10px] text-muted-foreground group-hover/stat:text-emerald-600">
              Responses
            </p>
          </Link>
          <Link
            to={`/analytics?asset=form&id=${form.id}`}
            className="text-center p-2 bg-muted/50 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer group/stat"
          >
            <div className="flex items-center justify-center gap-1 text-muted-foreground group-hover/stat:text-blue-600 mb-0.5">
              <BarChart3 className="h-3 w-3" />
            </div>
            <p className="text-sm font-bold tabular-nums group-hover/stat:text-blue-600">
              {responseRate}%
            </p>
            <p className="text-[10px] text-muted-foreground group-hover/stat:text-blue-600">Rate</p>
          </Link>
        </div>

        {/* Prominent Submissions Button */}
        {form.totalSubmissions > 0 && (
          <Link
            to={`/forms/${form.id}/submissions`}
            className="flex items-center justify-between w-full px-3 py-2 mb-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 transition-colors group/submissions"
          >
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              <span className="text-sm font-medium">
                View {form.totalSubmissions}{' '}
                {form.totalSubmissions === 1 ? 'Response' : 'Responses'}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 group-hover/submissions:translate-x-0.5 transition-transform" />
          </Link>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t">
          <code className="px-2 py-1 bg-muted rounded text-[10px] font-mono text-muted-foreground truncate max-w-[140px]">
            /f/{form.slug}
          </code>
          <div className="flex items-center gap-1">
            {/* Quick Actions */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onEdit(form)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit form</TooltipContent>
            </Tooltip>

            {form.isPublished && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={handleCopy}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy URL</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>Open form</TooltipContent>
                </Tooltip>
              </>
            )}

            {/* More Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(form)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Form
                </DropdownMenuItem>
                {form.totalSubmissions > 0 && (
                  <DropdownMenuItem asChild>
                    <Link to={`/forms/${form.id}/submissions`}>
                      <Inbox className="h-4 w-4 mr-2" />
                      View Submissions ({form.totalSubmissions})
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onTogglePublish(form)}>
                  {form.isPublished ? (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Unpublish
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Publish
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleResponses(form)}>
                  {form.isAcceptingResponses ? (
                    <>
                      <CircleDashed className="h-4 w-4 mr-2" />
                      Stop Accepting Responses
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Start Accepting Responses
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(form)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Loading skeleton
function FormSkeleton() {
  return (
    <Card className="rounded-2xl">
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <Skeleton className="h-11 w-11 rounded-xl" />
          <div className="flex-1">
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
        <div className="flex justify-between pt-3 border-t">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function FormsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openDeleteConfirmation } = useUIStore();
  const { getLimit, tierName } = useFeatures();

  // URL-synced state
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString.withDefault(''));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));

  // Form creation limit
  const formLimit = getLimit('form_creation');
  const hasUnlimitedForms = formLimit === null;

  // Fetch forms
  const { data, isLoading, error } = useQuery({
    queryKey: ['forms', { searchQuery, page }],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: '12',
        offset: ((page - 1) * 12).toString(),
      });
      if (searchQuery) params.set('search', searchQuery);

      const response = await apiFetch(`/api/forms?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch forms');
      }
      return response.json();
    },
  });

  // Toggle publish mutation
  const togglePublishMutation = useMutation({
    mutationFn: async (form: FormResponse) => {
      const endpoint = form.isPublished ? 'unpublish' : 'publish';
      const response = await apiFetch(`/api/forms/${form.id}/${endpoint}`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error(`Failed to ${endpoint} form`);
      return response.json();
    },
    onSuccess: (_, form) => {
      toast.success(form.isPublished ? 'Form unpublished' : 'Form published');
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Toggle accepting responses mutation
  const toggleResponsesMutation = useMutation({
    mutationFn: async (form: FormResponse) => {
      const response = await apiFetch(`/api/forms/${form.id}/toggle-responses`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to toggle responses');
      return response.json();
    },
    onSuccess: (_, form) => {
      toast.success(
        form.isAcceptingResponses
          ? 'Form stopped accepting responses'
          : 'Form is now accepting responses'
      );
      queryClient.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleEdit = (form: FormResponse) => {
    navigate(`/forms/${form.id}/edit`);
  };

  const handleDelete = (form: FormResponse) => {
    openDeleteConfirmation('form', form.id, form.name);
  };

  const forms: FormResponse[] = data?.data || [];
  const totalForms = data?.pagination?.total || 0;
  const totalPagesCount = Math.ceil(totalForms / 12);

  // Calculate stats
  const totalResponses = forms.reduce((sum, f) => sum + (f.totalSubmissions || 0), 0);
  const publishedCount = forms.filter((f) => f.isPublished).length;
  const activeCount = forms.filter((f) => f.isAcceptingResponses).length;

  // Check if at form creation limit
  const isAtLimit = !hasUnlimitedForms && formLimit !== null && totalForms >= formLimit;
  const usagePercent = hasUnlimitedForms
    ? 0
    : formLimit
      ? Math.min((totalForms / formLimit) * 100, 100)
      : 0;

  // Handle create form with limit check
  const handleCreateForm = () => {
    if (isAtLimit) {
      toast.error(
        `You've reached your ${tierName} plan limit of ${formLimit} forms. Upgrade to create more.`
      );
      return;
    }
    navigate('/forms/new');
  };

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <ClipboardList className="h-5 w-5 text-white" />
            </div>
            Forms
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Build forms and collect responses with attribution tracking
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={handleCreateForm}
            size="lg"
            disabled={isAtLimit}
            className="rounded-xl gap-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 disabled:opacity-50"
          >
            <Plus className="h-5 w-5" />
            Create Form
          </Button>
          {/* Usage indicator for users with limits */}
          {!hasUnlimitedForms && formLimit && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>
                {totalForms} / {formLimit} forms
              </span>
              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${usagePercent >= 100 ? 'bg-destructive' : usagePercent >= 80 ? 'bg-amber-500' : 'bg-primary'}`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
              {isAtLimit && (
                <Link to="/pricing" className="text-primary hover:underline font-medium">
                  Upgrade
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {totalForms > 0 && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <ClipboardList className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Forms</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalForms)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Published</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(publishedCount)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Accepting</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(activeCount)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Inbox className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Responses</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalResponses)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search forms by name…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="pl-12 h-12 rounded-xl"
          />
        </div>
      </div>

      {/* Forms Grid */}
      {error ? (
        <Card className="rounded-2xl py-12 border-destructive/20 bg-destructive/5">
          <CardContent className="text-center">
            <div className="h-24 w-24 rounded-3xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <ClipboardList className="h-12 w-12 text-destructive/50" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-destructive">Error Loading Forms</h3>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <FormSkeleton key={i} />
          ))}
        </div>
      ) : forms.length === 0 ? (
        searchQuery ? (
          <SearchEmptyState
            searchQuery={searchQuery}
            onClear={() => setSearchQuery('')}
            entityName="forms"
          />
        ) : (
          <OnboardingEmptyState
            icon={ClipboardList}
            title="Create your first form"
            steps={[
              { icon: FileText, text: 'Build forms with drag-and-drop fields' },
              { icon: Inbox, text: 'Collect responses with attribution tracking' },
              { icon: BarChart3, text: 'Analyze submissions and conversion rates' },
            ]}
            action={{
              label: 'Create your first form',
              onClick: handleCreateForm,
              icon: Plus,
            }}
          />
        )
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {forms.map((form) => (
              <FormCard
                key={form.id}
                form={form}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTogglePublish={(f) => togglePublishMutation.mutate(f)}
                onToggleResponses={(f) => toggleResponsesMutation.mutate(f)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPagesCount > 1 && (
            <div className="flex items-center justify-between py-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{(page - 1) * 12 + 1}</span> to{' '}
                <span className="font-medium">{Math.min(page * 12, totalForms)}</span> of{' '}
                <span className="font-medium">{totalForms}</span> forms
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-lg"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPagesCount}
                  className="rounded-lg"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
